import React from 'react';
import { useConfig } from '../../context/ConfigContext';
import styles from './PrivacyPolicyPage.module.scss';

interface PolicySection {
  title: string;
  content: string | string[];
}

const PrivacyPolicyPage = () => {
  const { config } = useConfig();
  const website_url = window.location.origin;

  const companyName: string = config?.companyName ?? 'Frenvinnov';
  const appName: string = config?.companyName
    ?  config.companyName
    : "de FrenchInnov";
  const contactEmail: string = 'contact@frenchinnov.fr';
  const websiteUrl: string = website_url ?? 'frenchinnov.fr';
  const lastUpdated: string = '16 Avril 2026';

  const sections: PolicySection[] = [
    {
      title: '1. Informations que nous collectons',
      content: [
        "1.1 Informations fournies directement par l'utilisateur : Nom d'utilisateur et informations de profil, adresse e-mail, et toute autre information que vous choisissez de fournir.",
        "1.2 Données de localisation : Nous pouvons collecter des informations sur votre localisation lorsque vous utilisez l'Application, avec votre consentement.",
        "1.3 Données d'utilisation : Informations sur la façon dont vous utilisez l'Application (pages visitées, fonctionnalités utilisées, temps passé).",
        "1.4 Données techniques : Type d'appareil, système d'exploitation, identifiants uniques de l'appareil, adresse IP, données de journal.",
      ],
    },
    {
      title: '2. Comment nous utilisons vos informations',
      content: [
        'Fournir et maintenir notre Application',
        'Améliorer et personnaliser votre expérience utilisateur',
        'Vous fournir des services basés sur la localisation (avec votre consentement)',
        "Analyser l'utilisation de l'Application pour améliorer ses fonctionnalités",
        'Vous envoyer des notifications et des mises à jour importantes',
        'Détecter, prévenir et résoudre les problèmes techniques',
        'Se conformer aux obligations légales',
      ],
    },
    {
      title: '3. Partage de vos informations',
      content: [
        'Nous ne vendons pas vos données personnelles. Nous pouvons partager vos informations dans les cas suivants :',
        "Fournisseurs de services : Avec des prestataires tiers qui nous aident à exploiter l'Application (hébergement, analyse, support client)",
        'Obligations légales : Si requis par la loi ou pour protéger nos droits',
        'Avec votre consentement : Nous pouvons partager vos informations avec votre accord explicite',
      ],
    },
    {
      title: '4. Services tiers',
      content: [
        "Notre Application peut utiliser des services tiers qui collectent des informations :",
        'Google Play Services',
        "Services d'analyse (Google Analytics, Firebase, etc.)",
        'Ces services ont leurs propres politiques de confidentialité. Nous vous encourageons à les consulter.',
      ],
    },
    {
      title: '5. Sécurité des données',
      content:
        "Nous prenons la sécurité de vos données au sérieux et utilisons des mesures techniques et organisationnelles appropriées pour protéger vos informations personnelles contre tout accès, modification, divulgation ou destruction non autorisés. Toutefois, aucune méthode de transmission sur Internet ou de stockage électronique n'est totalement sûre.",
    },
    {
      title: '6. Conservation des données',
      content:
        'Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos services et respecter nos obligations légales. Lorsque vos données ne sont plus nécessaires, nous les supprimons ou les anonymisons de manière sécurisée.',
    },
    {
      title: '7. Vos droits (RGPD)',
      content: [
        "Droit d'accès : Obtenir une copie de vos données personnelles",
        'Droit de rectification : Corriger vos données inexactes ou incomplètes',
        "Droit à l'effacement : Demander la suppression de vos données",
        'Droit à la limitation : Limiter le traitement de vos données',
        'Droit d\'opposition : Vous opposer au traitement de vos données',
        'Droit à la portabilité : Recevoir vos données dans un format structuré',
        "Droit de retirer votre consentement : À tout moment, sans affecter la légalité du traitement antérieur",
      ],
    },
    {
      title: '8. Données de localisation',
      content:
        "L'Application peut demander l'accès à votre localisation pour fournir des services basés sur la localisation. Vous pouvez activer ou désactiver les services de localisation à tout moment dans les paramètres de votre appareil. Le refus de fournir votre localisation peut limiter certaines fonctionnalités de l'Application.",
    },
    {
      title: '9. Protection des données des mineurs',
      content:
        "Notre Application n'est pas destinée aux personnes de moins de 13 ans. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants de moins de 13 ans. Si vous êtes parent ou tuteur et que vous pensez que votre enfant nous a fourni des données personnelles, veuillez nous contacter.",
    },
    {
      title: '10. Modifications de cette politique',
      content:
        'Nous pouvons mettre à jour notre Politique de confidentialité de temps à autre. Nous vous informerons de tout changement en publiant la nouvelle Politique de confidentialité sur cette page et en mettant à jour la date de "Dernière mise à jour". Nous vous recommandons de consulter régulièrement cette Politique de confidentialité.',
    },
    {
      title: '11. Transferts internationaux de données',
      content:
        "Vos informations peuvent être transférées et conservées sur des serveurs situés en dehors de votre pays de résidence. Nous prenons des mesures pour garantir que vos données sont traitées en toute sécurité et conformément à cette Politique de confidentialité.",
    },
  ];

  const logo = config?.hasDarkBackground
    ? config?.appDarkLogoImage
    : config?.appLightLogoImage || config?.appLogoImage;

  return (
    <div
      className={styles.page}
      style={{ '--primary-color': config?.primaryColor } as React.CSSProperties}
    >
      <div className={styles.container}>

        {/* ── Header ─────────────────────────────────── */}
        <header className={styles.header}>
          {logo && (
            <img src={logo} alt={`${appName} logo`} className={styles.logo} />
          )}
          <h1 className={styles.title}>Politique de Confidentialité</h1>
          {/* <p className={styles.subtitle}>
            {companyName} · {appName}
          </p> */}
          <span className={styles.badge}>Dernière mise à jour : {lastUpdated}</span>
        </header>

        {/* ── Intro ──────────────────────────────────── */}
        <div className={styles.introCard}>
          <p>
            <strong>{companyName}</strong> ("nous", "notre" ou "nos") exploite l'application
            mobile <strong>{appName}</strong> (ci-après dénommée "l'Application"). Cette page
            vous informe de nos politiques en matière de collecte, d'utilisation et de
            divulgation des données personnelles lorsque vous utilisez notre Application.
          </p>
        </div>

        {/* ── Sections ───────────────────────────────── */}
        <div className={styles.sections}>
          {sections.map((section, idx) => (
            <section key={idx} className={styles.section}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              {Array.isArray(section.content) ? (
                <ul className={styles.list}>
                  {section.content.map((item, i) => (
                    <li key={i} className={styles.listItem}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className={styles.paragraph}>{section.content}</p>
              )}
            </section>
          ))}
        </div>

        {/* ── Contact ────────────────────────────────── */}
        <div className={styles.contactCard}>
          <h2 className={styles.sectionTitle}>12. Contact</h2>
          <p className={styles.paragraph}>
            Si vous avez des questions concernant cette Politique de confidentialité ou nos
            pratiques en matière de données, veuillez nous contacter :
          </p>
          <div className={styles.contactLinks}>
            <a href={`mailto:${contactEmail}`} className={styles.contactLink}>
              <span className={styles.contactIcon}>✉</span>
              {contactEmail}
            </a>
            <a
              href={`https://${websiteUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.contactLink}
            >
              <span className={styles.contactIcon}>⊕</span>
              {websiteUrl}
            </a>
          </div>
        </div>

        {/* ── Consent ────────────────────────────────── */}
        <div className={styles.consentBanner}>
          <span className={styles.consentIcon}>✓</span>
          <p className={styles.consentText}>
            En utilisant notre Application, vous consentez à notre Politique de confidentialité
            et acceptez ses conditions.
          </p>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPolicyPage;